import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';
import { cn } from '../../utils/cn';
import { Inline } from '../inline/inline';

/**
 * A set of layered sections of content—known as tab panels—that are displayed one at a time.
 * @see https://ui.shadcn.com/docs/components/tabs
 */
const Tabs = TabsPrimitive.Root;

/**
 * Contains the triggers that are aligned along a single axis.
 */
const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { actionButtons?: React.ReactNode }
>(({ className, children, actionButtons, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn('inline-flex w-full items-center justify-between gap-2 rounded-xl bg-white px-5', className)}
    {...props}
  >
    <Inline justify="start" align="center" wrap="nowrap" className="shrink overflow-hidden">
      {children}
    </Inline>

    <div className="shrink-0">{actionButtons}</div>
  </TabsPrimitive.List>
));
TabsList.displayName = TabsPrimitive.List.displayName;

/**
 * The button that activates its associated content.
 */
const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      'group mt-1.5 inline-flex min-w-0 items-center justify-start whitespace-nowrap rounded-t-xl px-5 py-2.5 *:min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-seapoint-yellow focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-seapoint-gray-background-modal',
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

/**
 * Contains the content associated with each trigger.
 */
const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'rounded-xl bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
